import 'maplibre-gl/dist/maplibre-gl.css'
import maplibregl, {
  AttributionControl,
  type GeoJSONSource,
  type LngLatLike,
  Map as MapLibreMap,
  ScaleControl,
} from 'maplibre-gl'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  applyHighContrastDarkPatch,
  type BaseStyleId,
  getBaseStyle,
  isHighContrastDark,
} from '@/features/geolocation/utils/maplibre-styles'
import {
  CLUSTER_HEAT,
  defaultTrackColorFor,
  normalizeMarkerType,
} from '@/features/geolocation/utils/marker-palette'
import { buildAllPinImages } from '@/features/geolocation/utils/pin-markers'
import { clipTrackToTime } from '@/features/geolocation/utils/track-clip'
import { mapObjectSetId } from '@/features/source-snippets/object-set'
import { useCockpitStore } from '@/stores/cockpit-store'
import { useGeofenceStore } from '@/stores/geofence-store'
import type { GeoEntity, TrackPoint } from '@/stores/geolocation-store'
import { useGeolocationStore } from '@/stores/geolocation-store'
import { useTimelineStore } from '@/stores/timeline-store'
import { useTheme } from '@/theme'

const ENTITY_SOURCE_ID = 'entities'
const ENTITY_CLUSTER_LAYER = 'entities-cluster'
const ENTITY_CLUSTER_COUNT_LAYER = 'entities-cluster-count'
const ENTITY_POINT_LAYER = 'entities-point'
const ENTITY_SELECTED_HALO_LAYER = 'entities-selected-halo'
const ENTITY_SELECTED_HALO_OUTER_LAYER = 'entities-selected-halo-outer'

// Spider expander — when the user clicks a stack of markers at the same
// coordinate, we fan them out in a screen-space circle so each becomes
// individually clickable. Two layers: connector legs and spread pins.
const SPIDER_LEG_SOURCE = 'spider-legs'
const SPIDER_POINT_SOURCE = 'spider-points'
const SPIDER_LEG_LAYER = 'spider-legs-line'
const SPIDER_POINT_LAYER = 'spider-points-symbol'
const SPIDER_SELECTED_HALO_LAYER = 'spider-selected-halo'
const SPIDER_SELECTED_HALO_OUTER_LAYER = 'spider-selected-halo-outer'

const TRACKS_SOURCE_ID = 'tracks'
const TRACKS_LINE_LAYER = 'tracks-line'
const TRACKS_LINE_GLOW_LAYER = 'tracks-line-glow'

// Track-line color resolution: explicit per-entity override (set via the
// sidebar) wins, otherwise fall back to a type-based default. Vehicle red
// and aircraft cyan match the legacy Cesium scheme.
const TRACK_LINE_COLOR = [
  'coalesce',
  ['get', 'color'],
  [
    'match',
    ['get', 'type'],
    'vehicle',
    '#ff5252',
    'car',
    '#ff5252',
    'aircraft',
    '#00bfff',
    'plane',
    '#00bfff',
    '#4fc3f7',
  ],
] as unknown as maplibregl.ExpressionSpecification

const GEOFENCE_FILL_SOURCE = 'geofences-fill-source'
const GEOFENCE_LINE_SOURCE = 'geofences-line-source'
const GEOFENCE_FILL_LAYER = 'geofences-fill'
const GEOFENCE_LINE_LAYER = 'geofences-line'
const GEOFENCE_LABEL_LAYER = 'geofences-label'

const DRAW_FILL_SOURCE = 'draw-fill-source'
const DRAW_LINE_SOURCE = 'draw-line-source'
const DRAW_VERTEX_SOURCE = 'draw-vertex-source'
const DRAW_FILL_LAYER = 'draw-fill'
const DRAW_LINE_LAYER = 'draw-line'
const DRAW_VERTEX_LAYER = 'draw-vertex'
const DRAW_PREVIEW_LINE_LAYER = 'draw-preview-line'

function mapBaseLayer(b: string): BaseStyleId {
  if (b === 'satellite' || b === 'streets' || b === 'hybrid' || b === 'osm') return b
  if (b === 'dark') return 'dark'
  if (b === 'dark-hc') return 'dark-hc'
  return 'satellite'
}

interface MapLibreViewerProps {
  onMapReady?: (map: MapLibreMap) => void
  // Callback invoked on every cursor move with current map coords + zoom
  // — used to power the bottom-left coord HUD.
  onCursor?: (info: CursorInfo | null) => void
  // Fired every time the user taps a pin (also one that is already
  // selected) — lets a phone host bring the entity detail on screen.
  onEntityPick?: (entityId: string) => void
}

export interface CursorInfo {
  lng: number
  lat: number
  zoom: number
}

export function MapLibreViewer({ onMapReady, onCursor, onEntityPick }: MapLibreViewerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  // Read through a ref so the map's click handlers are not re-bound when the
  // host passes a new callback.
  const onEntityPickRef = useRef(onEntityPick)
  onEntityPickRef.current = onEntityPick
  const mapRef = useRef<MapLibreMap | null>(null)
  const [mapReady, setMapReady] = useState(false)
  const [imagesReady, setImagesReady] = useState(false)
  const [previewCursor, setPreviewCursor] = useState<[number, number] | null>(null)
  // Bumped after each setStyle finishes loading so data-push effects re-run.
  const [styleEpoch, setStyleEpoch] = useState(0)
  // Active spider stack — set when the user clicks a pile of overlapping
  // markers; their pins fan out in a circle so each is individually
  // clickable. `null` means no spider is open.
  const [spider, setSpider] = useState<{
    centerLngLat: [number, number]
    entityIds: string[]
  } | null>(null)

  const baseLayer = useGeolocationStore(s => s.baseLayer)
  const setBaseLayer = useGeolocationStore(s => s.setBaseLayer)
  const entities = useGeolocationStore(s => s.entities)
  const selectedEntityId = useGeolocationStore(s => s.selectedEntityId)
  const selectEntity = useGeolocationStore(s => s.selectEntity)

  // Basemap auto-follows the app theme. The store seeds with the matching
  // basemap at module init via `detectInitialBaseLayer`; this effect keeps
  // them in sync when the user toggles light ↔ dark at runtime, AND when
  // `theme === 'system'` the OS-level colour-scheme preference flips
  // underneath us (a matchMedia `change` event — the `theme` prop itself
  // stays `'system'` so the effect wouldn't otherwise re-run).
  //
  // We only swap when the *current* basemap is the OTHER theme's auto
  // default — i.e. the user hasn't explicitly picked something distinctive
  // like satellite or streets. Explicit choices stay sticky across theme
  // toggles; only the two "neutral" basemaps follow the theme.
  const { theme } = useTheme()
  const lastResolvedThemeRef = useRef<'dark' | 'light' | null>(null)
  useEffect(() => {
    const resolved: 'dark' | 'light' =
      theme === 'system'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : theme
    const prev = lastResolvedThemeRef.current
    lastResolvedThemeRef.current = resolved
    // Skip the first run — the store was already seeded with the right
    // basemap by `detectInitialBaseLayer`. Only react to actual changes.
    if (prev === null || prev === resolved) return
    const current = useGeolocationStore.getState().baseLayer
    if (resolved === 'light' && current === 'dark-hc') {
      setBaseLayer('osm')
    } else if (resolved === 'dark' && current === 'osm') {
      setBaseLayer('dark-hc')
    }
  }, [theme, setBaseLayer])
  const hiddenEntityTypes = useGeolocationStore(s => s.hiddenEntityTypes)
  const hiddenEntityIds = useGeolocationStore(s => s.hiddenEntityIds)
  const tracks = useGeolocationStore(s => s.tracks)
  const showTracks = useGeolocationStore(s => s.showTracks)
  const hiddenTrackEntityIds = useGeolocationStore(s => s.hiddenTrackEntityIds)
  const trackColors = useGeolocationStore(s => s.trackColors)
  const playheadTime = useTimelineStore(s => s.playheadTime)
  // Cross-view filters from the timeline store. `filterStart/filterEnd`
  // is the active timeline window; `visibleGraphNodeIds` is the set of
  // graph node ids that survived the active pattern filter (`null`
  // means no filter). `entityKeyToNodeId` maps a GeoEntity.id onto its
  // graph node id so we can match entities against `visibleGraphNodeIds`.
  const filterStart = useTimelineStore(s => s.filterStart)
  const filterEnd = useTimelineStore(s => s.filterEnd)
  const visibleGraphNodeIds = useTimelineStore(s => s.visibleGraphNodeIds)
  const entityKeyToNodeId = useTimelineStore(s => s.entityKeyToNodeId)

  const geofences = useGeofenceStore(s => s.geofences)
  const drawingMode = useGeofenceStore(s => s.drawingMode)
  const drawingVertices = useGeofenceStore(s => s.drawingVertices)
  const addVertex = useGeofenceStore(s => s.addVertex)
  const undoVertex = useGeofenceStore(s => s.undoVertex)
  const cancelDrawing = useGeofenceStore(s => s.cancelDrawing)
  const saveDrawing = useGeofenceStore(s => s.saveDrawing)

  // Initialise map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    const map = new MapLibreMap({
      container: containerRef.current,
      style: getBaseStyle(mapBaseLayer(baseLayer)),
      center: [14.4378, 50.0755] as LngLatLike,
      zoom: 11,
      attributionControl: false,
      hash: false,
    })
    map.addControl(new ScaleControl({ unit: 'metric' }), 'bottom-left')
    map.addControl(new AttributionControl({ compact: true }))

    map.on('load', () => {
      mapRef.current = map
      // Register pin images before flagging the map ready so the symbol
      // layer can resolve them on first render.
      void registerPinImages(map).then(() => {
        setImagesReady(true)
      })
      setMapReady(true)
      onMapReady?.(map)
    })

    // Lazy-register any pin- image MapLibre asks for. Acts as a safety
    // net for the styledata race after setStyle: even if our explicit
    // re-registration in the styledata callback hasn't completed yet, a
    // missing pin image triggers this handler and we backfill it.
    map.on('styleimagemissing', e => {
      const id = e.id
      if (!id?.startsWith('pin-')) return
      void buildAllPinImages().then(records => {
        const r = records.find(rec => rec.name === id)
        if (r && !map.hasImage(r.name)) {
          map.addImage(r.name, r.data, { pixelRatio: r.pixelRatio })
        }
      })
    })

    return () => {
      try {
        map.remove()
      } catch {
        /* context already gone */
      }
      mapRef.current = null
      setMapReady(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── First-load auto-fit on the densest cluster ────────────────────────
  // The default center is Prague at zoom 11 — useful with no data, jarring
  // after a refresh on a case whose entities sit elsewhere. We fit the
  // camera once per mount, but to the *concentration* of entities, not
  // their literal bounding box: a single far-flung outlier (a tagged photo
  // in Australia while the rest of the case is in Czechia) would otherwise
  // zoom us out to a near-world view.
  //
  // Heuristic — robust to scattered outliers, cheap to compute:
  //   1. Find the (lat, lon) median across all entities.
  //   2. Compute each entity's planar distance from that median (lat/lon
  //      treated as flat coords — accurate enough for a fitBounds within
  //      one country / region).
  //   3. Keep entities inside the 75th percentile of those distances.
  //   4. fitBounds to the kept subset.
  //
  // The cluster's median + 75% radius reliably captures the dominant
  // grouping while dropping the long tail. Subsequent entity-set changes
  // DON'T refit — that would yank the camera out from under the analyst.
  const didInitialFitRef = useRef(false)
  useEffect(() => {
    if (didInitialFitRef.current) return
    const map = mapRef.current
    if (!map || !mapReady) return
    if (entities.length === 0) return

    const bounds = computeConcentrationBounds(entities)
    if (!bounds) return

    didInitialFitRef.current = true
    map.fitBounds(
      [
        [bounds.minLng, bounds.minLat],
        [bounds.maxLng, bounds.maxLat],
      ],
      // No animation on the initial fit — the user is just opening the
      // page, jumping straight to the bounds reads as "this is where the
      // map starts" instead of an explicit camera move. `maxZoom: 14`
      // mirrors the Reset button so single-cluster cases don't zoom in to
      // street level.
      { padding: 80, maxZoom: 14, animate: false }
    )
  }, [entities, mapReady])

  // Switch base style. We force `diff: false` for a clean swap so
  // previous high-contrast paint overrides + custom sources/layers are
  // fully wiped before we re-create them. We listen on BOTH `styledata`
  // and `idle` (whichever fires first with the new style ready) and run
  // the setup exactly once. Inside setup we push the current entity
  // FeatureCollection directly from the store so we don't depend on a
  // React effect re-run timing — that was the original race that left
  // markers blank when going dark+ → light.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady) return
    const style = getBaseStyle(mapBaseLayer(baseLayer))
    if (typeof style === 'string') {
      map.setStyle(style, { diff: false })
    } else {
      map.setStyle(style, { diff: false })
    }
    const styleId = mapBaseLayer(baseLayer)

    let done = false
    const setup = () => {
      if (done) return
      if (!map.isStyleLoaded()) return
      // Wrap layer/source creation in try/catch — MapLibre's
      // `_checkLoaded` is stricter than `isStyleLoaded()` and can throw
      // during the brief window after the new style first reports loaded
      // but before its internal state is fully settled. If we hit that,
      // bail out and try again on the next event tick.
      try {
        if (isHighContrastDark(styleId)) {
          applyHighContrastDarkPatch(map)
        }
        ensureTrackLayers(map)
        ensureEntityLayers(map)
        ensureSpiderLayers(map)
        ensureGeofenceLayers(map)
        ensureDrawLayers(map)
      } catch {
        return
      }
      done = true
      map.off('styledata', setup)
      map.off('idle', setup)

      // Push entity + tracks + geofence + selected-halo data immediately,
      // reading the latest store state directly. Belt-and-suspenders against
      // the React useEffect re-run timing.
      const geoState = useGeolocationStore.getState()
      const fenceState = useGeofenceStore.getState()
      const tlState = useTimelineStore.getState()
      // Apply the same filter pipeline as the React useMemo so a fresh
      // setStyle doesn't accidentally re-render entities the rest of the
      // app has filtered out.
      let visEntities = geoState.entities
      // Tracks-layer temporal scope: when 'full', tracked entities bypass the
      // timeline filter so the user always sees their full trajectory. When
      // 'follow-scrubber' (legacy) or 'custom-range', the filter applies.
      if (tlState.filterStart && tlState.filterEnd && geoState.tracksTemporalScope !== 'full') {
        const fStart = Date.parse(tlState.filterStart)
        const fEnd = Date.parse(tlState.filterEnd)
        if (!Number.isNaN(fStart) && !Number.isNaN(fEnd)) {
          visEntities = visEntities.filter(e => {
            const trackData = geoState.tracks[e.id]
            if (!trackData || trackData.length === 0) return true
            return trackData.some(pt => {
              const t = Date.parse(pt.timestamp)
              return t >= fStart && t <= fEnd
            })
          })
        }
      }
      if (tlState.visibleGraphNodeIds !== null) {
        const ids = tlState.visibleGraphNodeIds
        visEntities = visEntities.filter(e => {
          const nodeId = mapObjectSetId(e.id, tlState.entityKeyToNodeId)
          return nodeId != null && ids.has(nodeId)
        })
      }
      if (geoState.hiddenEntityTypes.size > 0) {
        visEntities = visEntities.filter(e => !geoState.hiddenEntityTypes.has(e.label ?? e.type))
      }
      if (geoState.hiddenEntityIds.size > 0) {
        visEntities = visEntities.filter(e => !geoState.hiddenEntityIds.has(e.id))
      }
      const phMs = tlState.playheadTime ? Date.parse(tlState.playheadTime) : null
      const tsMs: Record<string, number[]> = {}
      for (const id of Object.keys(geoState.tracks)) {
        tsMs[id] = geoState.tracks[id].map(p => Date.parse(p.timestamp))
      }
      ;(map.getSource(ENTITY_SOURCE_ID) as GeoJSONSource | undefined)?.setData(
        entitiesToFeatureCollection(visEntities, geoState.tracks, tsMs, phMs)
      )
      ;(map.getSource(TRACKS_SOURCE_ID) as GeoJSONSource | undefined)?.setData(
        buildTracksFeatureCollection({
          entities: visEntities,
          tracks: geoState.tracks,
          trackTimestampsMs: tsMs,
          hiddenTrackEntityIds: geoState.hiddenTrackEntityIds,
          trackColors: geoState.trackColors,
          showTracks: geoState.showTracks,
          playheadMs: phMs,
        })
      )
      ;(map.getSource(GEOFENCE_FILL_SOURCE) as GeoJSONSource | undefined)?.setData(
        geofencesToFillFc(fenceState.geofences)
      )
      ;(map.getSource(GEOFENCE_LINE_SOURCE) as GeoJSONSource | undefined)?.setData(
        geofencesToLineFc(fenceState.geofences)
      )
      // Apply the halo filter for current selection.
      // biome-ignore lint/suspicious/noExplicitAny: maplibre filter typing
      const haloFilter: any =
        geoState.selectedEntityId != null
          ? ['all', ['!', ['has', 'point_count']], ['==', ['get', 'id'], geoState.selectedEntityId]]
          : ['==', ['literal', '__none__'], ['literal', '__match__']]
      try {
        map.setFilter(ENTITY_SELECTED_HALO_OUTER_LAYER, haloFilter)
        map.setFilter(ENTITY_SELECTED_HALO_LAYER, haloFilter)
      } catch {
        /* layers not yet created on first render */
      }

      setImagesReady(true)
      setStyleEpoch(e => e + 1)
      void registerPinImages(map)
    }

    map.on('styledata', setup)
    map.on('idle', setup)

    return () => {
      done = true
      map.off('styledata', setup)
      map.off('idle', setup)
    }
  }, [baseLayer, mapReady])

  // Push entity GeoJSON (filtered by visibility) to the cluster source.
  // Filters are applied in the same order as the legacy Cesium viewer so
  // the Geo tab shows exactly the slice that the rest of the app has
  // narrowed down to:
  //   1. Time window  — entities whose track lies inside [filterStart,
  //      filterEnd]; entities without track data always pass.
  //   2. Graph filter — when a pattern filter is active in the
  //      Investigate context, only entities whose graph-node id is in
  //      `visibleGraphNodeIds` survive.
  //   3. Type-layer  — `hiddenEntityTypes` from the Layers popover.
  const tracksTemporalScope = useGeolocationStore(s => s.tracksTemporalScope)
  const visibleEntities = useMemo(() => {
    let result = entities
    // Single canonical filter window — `timeline-store.filterStart` /
    // `filterEnd`. Drives both the animated-track scrubbing and the
    // time-anchored-pin culling so the bottom TimeRangeFilter has one
    // place to write to and every consumer (Cesium clock, maplibre,
    // anything else) reads it from the same field.
    if (filterStart && filterEnd) {
      const fStart = Date.parse(filterStart)
      const fEnd = Date.parse(filterEnd)
      if (!Number.isNaN(fStart) && !Number.isNaN(fEnd)) {
        result = result.filter(e => {
          // Animated tracks: hide unless any track point falls in window.
          // Bypassed when the user explicitly asked the tracks layer to
          // ignore the scrubber (`tracksTemporalScope === 'full'`).
          if (e.mapRenderMode === 'animated_track') {
            if (tracksTemporalScope === 'full') return true
            const trackData = tracks[e.id]
            if (!trackData || trackData.length === 0) return true
            return trackData.some(pt => {
              const t = Date.parse(pt.timestamp)
              return t >= fStart && t <= fEnd
            })
          }
          // Time-anchored pins: hide unless `_temporal_min` / `_temporal_max`
          // (backend-stamped) overlaps the window. Pins with no temporal
          // stamp stay visible — a missing timestamp is never grounds to hide.
          if (e.mapRenderMode === 'time_anchored_pin') {
            const props = e.properties as Record<string, unknown> | undefined
            const minRaw = props?._temporal_min
            const maxRaw = props?._temporal_max
            const tMin =
              typeof minRaw === 'number'
                ? minRaw
                : typeof minRaw === 'string'
                  ? Date.parse(minRaw)
                  : Number.NaN
            const tMax =
              typeof maxRaw === 'number'
                ? maxRaw
                : typeof maxRaw === 'string'
                  ? Date.parse(maxRaw)
                  : Number.NaN
            const lo = Number.isNaN(tMin) ? (Number.isNaN(tMax) ? null : tMax) : tMin
            const hi = Number.isNaN(tMax) ? (Number.isNaN(tMin) ? null : tMin) : tMax
            if (lo === null || hi === null) return true
            return hi >= fStart && lo <= fEnd
          }
          // Static pins / unknown render modes are time-agnostic.
          return true
        })
      }
    }
    if (visibleGraphNodeIds !== null) {
      result = result.filter(e => {
        const nodeId = mapObjectSetId(e.id, entityKeyToNodeId)
        return nodeId != null && visibleGraphNodeIds.has(nodeId)
      })
    }
    if (hiddenEntityTypes.size > 0) {
      result = result.filter(e => !hiddenEntityTypes.has(e.label ?? e.type))
    }
    if (hiddenEntityIds.size > 0) {
      result = result.filter(e => !hiddenEntityIds.has(e.id))
    }
    return result
  }, [
    entities,
    tracks,
    filterStart,
    filterEnd,
    visibleGraphNodeIds,
    entityKeyToNodeId,
    hiddenEntityTypes,
    hiddenEntityIds,
    tracksTemporalScope,
  ])

  const playheadMs = useMemo(() => {
    if (!playheadTime) return null
    const v = Date.parse(playheadTime)
    return Number.isNaN(v) ? null : v
  }, [playheadTime])

  // Pre-parse track timestamps once per `tracks` change so the per-tick
  // clipping doesn't re-parse ISO strings inside its binary search.
  const trackTimestampsMs = useMemo(() => {
    const out: Record<string, number[]> = {}
    for (const id of Object.keys(tracks)) {
      out[id] = tracks[id].map(p => Date.parse(p.timestamp))
    }
    return out
  }, [tracks])

  // Animated entities are hidden when the playhead is before their first
  // sample (mirrors Cesium's NONE backward extrapolation).
  const entityFc = useMemo<GeoJSON.FeatureCollection>(() => {
    return entitiesToFeatureCollection(visibleEntities, tracks, trackTimestampsMs, playheadMs)
  }, [visibleEntities, tracks, trackTimestampsMs, playheadMs])

  const tracksFc = useMemo<GeoJSON.FeatureCollection>(() => {
    return buildTracksFeatureCollection({
      entities: visibleEntities,
      tracks,
      trackTimestampsMs,
      hiddenTrackEntityIds,
      trackColors,
      showTracks,
      playheadMs,
    })
  }, [
    visibleEntities,
    tracks,
    trackTimestampsMs,
    hiddenTrackEntityIds,
    trackColors,
    showTracks,
    playheadMs,
  ])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady || !imagesReady) return
    try {
      ensureTrackLayers(map)
      ensureEntityLayers(map)
    } catch {
      // Style still settling after a switch; the setStyle effect will
      // re-bump styleEpoch on idle and we'll re-run successfully.
      return
    }
    ;(map.getSource(ENTITY_SOURCE_ID) as GeoJSONSource | undefined)?.setData(entityFc)
    ;(map.getSource(TRACKS_SOURCE_ID) as GeoJSONSource | undefined)?.setData(tracksFc)

    // Halo filter: render the halo only when the selected entity is
    // currently un-clustered (i.e. visible as an individual pin). When
    // it gets clustered on zoom-out, the filter matches no feature so
    // the halo disappears with it. When the selected entity is part of an
    // open spider, hand the highlight off to the spider halo layers
    // (which sit on the spread source and follow the fanned-out pin).
    const isSelInSpider =
      spider != null && selectedEntityId != null && spider.entityIds.includes(selectedEntityId)
    // biome-ignore lint/suspicious/noExplicitAny: maplibre filter expression typing is permissive
    const noopFilter: any = ['==', ['literal', '__none__'], ['literal', '__match__']]
    // biome-ignore lint/suspicious/noExplicitAny: maplibre filter expression typing is permissive
    const haloFilter: any =
      selectedEntityId != null && !isSelInSpider
        ? ['all', ['!', ['has', 'point_count']], ['==', ['get', 'id'], selectedEntityId]]
        : noopFilter
    // biome-ignore lint/suspicious/noExplicitAny: maplibre filter expression typing is permissive
    const spiderHaloFilter: any = isSelInSpider
      ? ['==', ['get', 'id'], selectedEntityId]
      : noopFilter
    try {
      map.setFilter(ENTITY_SELECTED_HALO_OUTER_LAYER, haloFilter)
      map.setFilter(ENTITY_SELECTED_HALO_LAYER, haloFilter)
      map.setFilter(SPIDER_SELECTED_HALO_OUTER_LAYER, spiderHaloFilter)
      map.setFilter(SPIDER_SELECTED_HALO_LAYER, spiderHaloFilter)
    } catch {
      /* layers may not exist yet on very first render */
    }
  }, [entityFc, tracksFc, selectedEntityId, spider, mapReady, imagesReady, styleEpoch])

  // Click handlers: cluster expands, point selects.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady) return
    const onClusterClick = (e: maplibregl.MapMouseEvent) => {
      if (useGeofenceStore.getState().drawingMode === 'drawing') return
      const features = map.queryRenderedFeatures(e.point, { layers: [ENTITY_CLUSTER_LAYER] })
      const f = features[0]
      const clusterId = f?.properties?.cluster_id
      if (clusterId == null) return
      const source = map.getSource(ENTITY_SOURCE_ID) as GeoJSONSource | undefined
      if (!source) return
      const pointCount = (f.properties?.point_count as number) ?? 0
      source
        .getClusterExpansionZoom(clusterId as number)
        .then(async zoom => {
          const geom = f.geometry as GeoJSON.Point
          const coord = geom.coordinates as [number, number]
          // If expanding wouldn't actually move us (members co-located at the
          // same lat/lng), zoom in does nothing useful — fan them out as a
          // spider directly. Threshold: a single zoom step gain.
          if (zoom <= map.getZoom() + 0.25) {
            try {
              const leaves = (await source.getClusterLeaves(
                clusterId as number,
                pointCount || 100,
                0
              )) as Array<{ properties?: { id?: string } }>
              const ids = leaves
                .map(l => l.properties?.id)
                .filter((id): id is string => typeof id === 'string')
              if (ids.length > 1) {
                setSpider({ centerLngLat: coord, entityIds: ids })
                return
              }
            } catch {
              /* fall through to easeTo */
            }
          }
          map.easeTo({ center: coord, zoom })
        })
        .catch(() => {
          /* swallow — cluster may have changed */
        })
    }
    const onPointClick = (
      e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }
    ) => {
      if (useGeofenceStore.getState().drawingMode === 'drawing') return
      // Two-stage detection:
      //   1) Find which pin the user actually clicked (tight pixel hit-test).
      //   2) Find every other pin in the visible viewport whose coord is
      //      "essentially the same place" (≈20 m tolerance) as the clicked
      //      pin. We compare in degrees — pixel-bbox queries break down when
      //      a MobileDevice's last waypoint vs. the BTSStation's static
      //      record have e.g. 0.00007° of backend rounding noise (≈8 m,
      //      enough to land outside any reasonable pixel pad once zoomed in).
      //   If only one entity matches, just select it; otherwise fan them out.
      const hitFeats = e.features?.length
        ? e.features
        : map.queryRenderedFeatures(e.point, { layers: [ENTITY_POINT_LAYER] })
      const hit = hitFeats?.[0]
      if (!hit) {
        setSpider(null)
        return
      }
      const hitId = hit.properties?.id as string | undefined
      const hitCoord = (hit.geometry as GeoJSON.Point).coordinates as [number, number]
      if (!hitId) return
      const COORD_TOL = 0.0002 // ≈20 m latitude / ≈14 m longitude near 50°N
      const PAD_PX = 40
      const bbox: [maplibregl.PointLike, maplibregl.PointLike] = [
        [e.point.x - PAD_PX, e.point.y - PAD_PX],
        [e.point.x + PAD_PX, e.point.y + PAD_PX],
      ]
      const candidates = map.queryRenderedFeatures(bbox, { layers: [ENTITY_POINT_LAYER] })
      const ids: string[] = []
      for (const f of candidates) {
        const id = f.properties?.id as string | undefined
        if (!id || ids.includes(id)) continue
        const c = (f.geometry as GeoJSON.Point).coordinates as [number, number]
        const dLng = c[0] - hitCoord[0]
        const dLat = c[1] - hitCoord[1]
        if (Math.abs(dLng) > COORD_TOL || Math.abs(dLat) > COORD_TOL) continue
        ids.push(id)
      }
      if (!ids.includes(hitId)) ids.push(hitId)
      if (ids.length > 1) {
        setSpider({ centerLngLat: hitCoord, entityIds: ids })
      } else {
        selectEntity(hitId)
        setSpider(null)
        onEntityPickRef.current?.(hitId)
      }
    }
    const onSpiderClick = (
      e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }
    ) => {
      if (useGeofenceStore.getState().drawingMode === 'drawing') return
      const id = e.features?.[0]?.properties?.id as string | undefined
      if (id) {
        selectEntity(id)
        setSpider(null)
        onEntityPickRef.current?.(id)
      }
    }
    const onEnter = () => {
      if (useGeofenceStore.getState().drawingMode === 'drawing') return
      map.getCanvas().style.cursor = 'pointer'
    }
    const onLeave = () => {
      if (useGeofenceStore.getState().drawingMode === 'drawing') return
      map.getCanvas().style.cursor = ''
    }

    // Background click — deselect when the user clicks empty map area. The
    // layer-specific click handlers above fire FIRST for clicks on a pin /
    // cluster / spider point; this general handler then queries the same
    // layers and only deselects when none were hit. Matches the Cesium
    // viewer's LEFT_CLICK handler and the cytoscape background-tap behavior.
    const onBackgroundClick = (e: maplibregl.MapMouseEvent) => {
      if (useGeofenceStore.getState().drawingMode === 'drawing') return
      const hits = map.queryRenderedFeatures(e.point, {
        layers: [ENTITY_POINT_LAYER, ENTITY_CLUSTER_LAYER, SPIDER_POINT_LAYER],
      })
      if (hits.length === 0) {
        selectEntity(null)
      }
    }

    map.on('click', ENTITY_CLUSTER_LAYER, onClusterClick)
    map.on('click', ENTITY_POINT_LAYER, onPointClick)
    map.on('click', SPIDER_POINT_LAYER, onSpiderClick)
    map.on('click', onBackgroundClick)
    map.on('mouseenter', ENTITY_CLUSTER_LAYER, onEnter)
    map.on('mouseleave', ENTITY_CLUSTER_LAYER, onLeave)
    map.on('mouseenter', ENTITY_POINT_LAYER, onEnter)
    map.on('mouseleave', ENTITY_POINT_LAYER, onLeave)
    map.on('mouseenter', SPIDER_POINT_LAYER, onEnter)
    map.on('mouseleave', SPIDER_POINT_LAYER, onLeave)

    return () => {
      map.off('click', ENTITY_CLUSTER_LAYER, onClusterClick)
      map.off('click', ENTITY_POINT_LAYER, onPointClick)
      map.off('click', SPIDER_POINT_LAYER, onSpiderClick)
      map.off('click', onBackgroundClick)
      map.off('mouseenter', ENTITY_CLUSTER_LAYER, onEnter)
      map.off('mouseleave', ENTITY_CLUSTER_LAYER, onLeave)
      map.off('mouseenter', ENTITY_POINT_LAYER, onEnter)
      map.off('mouseleave', ENTITY_POINT_LAYER, onLeave)
      map.off('mouseenter', SPIDER_POINT_LAYER, onEnter)
      map.off('mouseleave', SPIDER_POINT_LAYER, onLeave)
    }
  }, [mapReady, selectEntity])

  // Spider geometry — when `spider` is set, fan its entities out around
  // the stack centre in a screen-space circle, and filter the main pin
  // layer to hide the original stacked features (so they don't overlap
  // the spider pins). Recomputes whenever the spider state, zoom, or
  // map move changes (so the spread stays anchored as user pans/zooms).
  //
  // PERF: the `move` listener is only attached while a spider is OPEN.
  // Previously we re-projected and called `setData` on every map frame
  // even with no spider, which competed with `flyTo` and `entityFc`
  // playhead rebuilds during scrubber playback and produced visible jank.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady) return

    // No spider open → ensure the spread sources are empty and the entity
    // layer's filter is back to its default; then exit. We do NOT attach a
    // `move` listener in this branch, so fly-to / playback frames don't
    // trigger any spider work.
    if (!spider) {
      const legSrc = map.getSource(SPIDER_LEG_SOURCE) as GeoJSONSource | undefined
      const ptSrc = map.getSource(SPIDER_POINT_SOURCE) as GeoJSONSource | undefined
      legSrc?.setData({ type: 'FeatureCollection', features: [] })
      ptSrc?.setData({ type: 'FeatureCollection', features: [] })
      try {
        map.setFilter(ENTITY_POINT_LAYER, ['!', ['has', 'point_count']])
      } catch {
        /* layer may not exist yet */
      }
      return
    }

    const apply = () => {
      const legSrc = map.getSource(SPIDER_LEG_SOURCE) as GeoJSONSource | undefined
      const ptSrc = map.getSource(SPIDER_POINT_SOURCE) as GeoJSONSource | undefined
      if (!legSrc || !ptSrc) return

      const center = map.project(spider.centerLngLat)
      const count = spider.entityIds.length
      // 60 px ring for ≤8 markers, slightly larger when more.
      const radius = count <= 8 ? 60 : 80
      const angleOffset = -Math.PI / 2 // first pin straight up
      const entitiesById = new Map(entities.map(e => [e.id, e]))
      const legFeatures: GeoJSON.Feature[] = []
      const ptFeatures: GeoJSON.Feature[] = []
      for (let i = 0; i < count; i++) {
        const id = spider.entityIds[i]
        const entity = entitiesById.get(id)
        if (!entity) continue
        const angle = angleOffset + (2 * Math.PI * i) / count
        const x = center.x + radius * Math.cos(angle)
        const y = center.y + radius * Math.sin(angle)
        const lngLat = map.unproject([x, y])
        const coord: [number, number] = [lngLat.lng, lngLat.lat]
        legFeatures.push({
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: [spider.centerLngLat, coord] },
        })
        ptFeatures.push({
          type: 'Feature',
          properties: {
            id: entity.id,
            name: entity.name,
            type: normalizeMarkerType(entity.label ?? entity.type),
            // Carry the dimmed flag so bare Locations stay dimmed when fanned
            // out of a cluster, matching the un-spidered point layer.
            dimmed: entity.dimmed === true,
          },
          geometry: { type: 'Point', coordinates: coord },
        })
      }
      legSrc.setData({ type: 'FeatureCollection', features: legFeatures })
      ptSrc.setData({ type: 'FeatureCollection', features: ptFeatures })

      // Hide the original stacked features so they don't sit under the
      // spread pins. We `match` against the spider id list and keep only
      // unclustered features whose id ISN'T in the spider.
      try {
        // biome-ignore lint/suspicious/noExplicitAny: maplibre filter typing
        const filter: any = [
          'all',
          ['!', ['has', 'point_count']],
          ['!', ['in', ['get', 'id'], ['literal', spider.entityIds]]],
        ]
        map.setFilter(ENTITY_POINT_LAYER, filter)
      } catch {
        /* style still settling */
      }
    }

    apply()
    // Re-apply on map move/zoom so the spread stays at a fixed pixel
    // offset around the stack centre.
    map.on('move', apply)
    return () => {
      map.off('move', apply)
    }
  }, [spider, entities, mapReady, styleEpoch])

  // Close the spider on background click, on a zoom change (so the
  // spread doesn't outlive a navigation), and on Escape.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady || !spider) return
    const onMapClick = (e: maplibregl.MapMouseEvent) => {
      // Ignore clicks on the spider points themselves — those select.
      const hits = map.queryRenderedFeatures(e.point, {
        layers: [SPIDER_POINT_LAYER, ENTITY_POINT_LAYER, ENTITY_CLUSTER_LAYER],
      })
      if (hits.length === 0) setSpider(null)
    }
    const onZoom = () => setSpider(null)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSpider(null)
    }
    map.on('click', onMapClick)
    map.on('zoomstart', onZoom)
    window.addEventListener('keydown', onKey)
    return () => {
      map.off('click', onMapClick)
      map.off('zoomstart', onZoom)
      window.removeEventListener('keydown', onKey)
    }
  }, [spider, mapReady])

  // Cursor → HUD coords.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady || !onCursor) return
    const onMove = (e: maplibregl.MapMouseEvent) => {
      onCursor({ lng: e.lngLat.lng, lat: e.lngLat.lat, zoom: map.getZoom() })
    }
    const onLeave = () => onCursor(null)
    map.on('mousemove', onMove)
    map.on('mouseout', onLeave)
    return () => {
      map.off('mousemove', onMove)
      map.off('mouseout', onLeave)
    }
  }, [mapReady, onCursor])

  // Geofence rendering (persisted).
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady) return
    try {
      ensureGeofenceLayers(map)
    } catch {
      return
    }
    ;(map.getSource(GEOFENCE_FILL_SOURCE) as GeoJSONSource | undefined)?.setData(
      geofencesToFillFc(geofences)
    )
    ;(map.getSource(GEOFENCE_LINE_SOURCE) as GeoJSONSource | undefined)?.setData(
      geofencesToLineFc(geofences)
    )
  }, [geofences, mapReady, styleEpoch])

  // Drawing: click to add vertex, double-click to finish, Esc to cancel,
  // Backspace to undo last vertex.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady) return

    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (useGeofenceStore.getState().drawingMode !== 'drawing') return
      addVertex({ longitude: e.lngLat.lng, latitude: e.lngLat.lat })
    }
    const onDoubleClick = (e: maplibregl.MapMouseEvent) => {
      if (useGeofenceStore.getState().drawingMode !== 'drawing') return
      e.preventDefault()
      const verts = useGeofenceStore.getState().drawingVertices
      if (verts.length < 3) return
      const defaultName = `Geofence ${useGeofenceStore.getState().geofences.length + 1}`
      saveDrawing(defaultName, useGeofenceStore.getState().defaultFenceHeight, false)
    }
    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (useGeofenceStore.getState().drawingMode !== 'drawing') return
      setPreviewCursor([e.lngLat.lng, e.lngLat.lat])
    }
    map.on('click', onClick)
    map.on('dblclick', onDoubleClick)
    map.on('mousemove', onMouseMove)
    return () => {
      map.off('click', onClick)
      map.off('dblclick', onDoubleClick)
      map.off('mousemove', onMouseMove)
    }
  }, [mapReady, addVertex, saveDrawing])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady) return
    if (drawingMode === 'drawing') {
      map.doubleClickZoom.disable()
      map.getCanvas().style.cursor = 'crosshair'
    } else {
      map.doubleClickZoom.enable()
      map.getCanvas().style.cursor = ''
      setPreviewCursor(null)
    }
  }, [drawingMode, mapReady])

  useEffect(() => {
    if (drawingMode !== 'drawing') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cancelDrawing()
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault()
        undoVertex()
      } else if (e.key === 'Enter') {
        const verts = useGeofenceStore.getState().drawingVertices
        if (verts.length >= 3) {
          const name = `Geofence ${useGeofenceStore.getState().geofences.length + 1}`
          saveDrawing(name, useGeofenceStore.getState().defaultFenceHeight, false)
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [drawingMode, cancelDrawing, undoVertex, saveDrawing])

  // Push in-progress draw geometry to the map.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady) return
    try {
      ensureDrawLayers(map)
    } catch {
      return
    }

    const coords: [number, number][] = drawingVertices.map(v => [v.longitude, v.latitude])

    const vertexFc: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: coords.map(c => ({
        type: 'Feature',
        properties: {},
        geometry: { type: 'Point', coordinates: c },
      })),
    }
    const fillFc: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features:
        coords.length >= 3
          ? [
              {
                type: 'Feature',
                properties: {},
                geometry: { type: 'Polygon', coordinates: [closeRing(coords)] },
              },
            ]
          : [],
    }
    const lineFc: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features:
        coords.length >= 2
          ? [
              {
                type: 'Feature',
                properties: {},
                geometry: { type: 'LineString', coordinates: coords },
              },
            ]
          : [],
    }
    const previewFc: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features:
        drawingMode === 'drawing' && previewCursor && coords.length >= 1
          ? [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: [coords[coords.length - 1], previewCursor],
                },
              },
            ]
          : [],
    }

    ;(map.getSource(DRAW_VERTEX_SOURCE) as GeoJSONSource | undefined)?.setData(vertexFc)
    ;(map.getSource(DRAW_FILL_SOURCE) as GeoJSONSource | undefined)?.setData(fillFc)
    ;(map.getSource(DRAW_LINE_SOURCE) as GeoJSONSource | undefined)?.setData(lineFc)
    ;(map.getSource(`${DRAW_LINE_SOURCE}-preview`) as GeoJSONSource | undefined)?.setData(previewFc)
  }, [drawingVertices, drawingMode, previewCursor, mapReady, styleEpoch])

  // Fly to selected entity. For trackables we honour the current playhead so
  // the camera lands on where the entity actually is right now (not its first
  // / last waypoint). Playback state is read via refs so this effect only
  // fires on selection / mount changes — re-running on every playhead tick
  // would cause continuous flyTo and tank perf during playback.
  const playbackPositionRef = useRef({
    tracks,
    trackTimestampsMs,
    playheadMs,
  })
  playbackPositionRef.current = { tracks, trackTimestampsMs, playheadMs }
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady || !selectedEntityId) return
    const e = entities.find(en => en.id === selectedEntityId)
    if (!e) return
    const { tracks: rTracks, trackTimestampsMs: rTs, playheadMs: rPh } = playbackPositionRef.current
    // Mirror entitiesToFeatureCollection's fallback chain so flyTo lands on
    // the SAME pixel where the marker is actually rendered:
    //   1. playhead-interpolated currentPos (entity is "live")
    //   2. last waypoint (when playhead is null or before the entity's first
    //      observation — same fallback the renderer uses)
    //   3. entity.lat/lng from the store (no track at all)
    let coords: [number, number] = [e.longitude, e.latitude]
    const track = rTracks[e.id]
    const ts = rTs[e.id]
    if (track && ts && track.length >= 2) {
      let resolved: [number, number] | null = null
      if (rPh != null) {
        const { currentPos } = clipTrackToTime(track, ts, rPh)
        if (currentPos) resolved = currentPos
      }
      if (!resolved) {
        const last = track[track.length - 1]
        resolved = [last.longitude, last.latitude]
      }
      coords = resolved
    }
    map.flyTo({ center: coords, zoom: Math.max(map.getZoom(), 14) })
  }, [selectedEntityId, entities, mapReady])

  // Map target from cockpit store (set by agent tools like ShowEntitiesAtLocation)
  const mapTarget = useCockpitStore(s => s.mapTarget)
  const setMapTarget = useCockpitStore(s => s.setMapTarget)

  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady || !mapTarget) return
    map.flyTo({
      center: [mapTarget.lon, mapTarget.lat],
      zoom: mapTarget.zoom,
      duration: 700,
    })
    // Clear after flying (one-shot)
    setMapTarget(null)
  }, [mapTarget, mapReady, setMapTarget])

  return (
    <div
      ref={containerRef}
      className="[&_.maplibregl-ctrl-attrib]:bg-black/60 [&_.maplibregl-ctrl-attrib]:text-white/70 [&_.maplibregl-ctrl-attrib_a]:text-white/80"
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        background: '#0a0a0c',
      }}
    />
  )
}

/**
 * Bounding box around the densest grouping of entities. Picks the median
 * point and keeps everything within the 75th percentile of distance from
 * it; the long tail of scattered outliers is dropped so a Czech-only case
 * with one Australia geotag doesn't end up framed to half the planet.
 *
 * Treats lat/lon as flat planar coordinates — fine for fitBounds at the
 * resolutions we care about (single country / region). The map projection
 * deals with the actual on-screen framing.
 */
function computeConcentrationBounds(
  entities: GeoEntity[]
): { minLng: number; minLat: number; maxLng: number; maxLat: number } | null {
  if (entities.length === 0) return null

  const allBounds = () => {
    let minLng = Infinity
    let maxLng = -Infinity
    let minLat = Infinity
    let maxLat = -Infinity
    for (const e of entities) {
      if (e.longitude < minLng) minLng = e.longitude
      if (e.longitude > maxLng) maxLng = e.longitude
      if (e.latitude < minLat) minLat = e.latitude
      if (e.latitude > maxLat) maxLat = e.latitude
    }
    if (!Number.isFinite(minLng)) return null
    return { minLng, minLat, maxLng, maxLat }
  }

  // Too few to meaningfully filter — fitBounds straight to all of them.
  if (entities.length <= 4) return allBounds()

  // Median lat / lon — robust to outliers compared to the arithmetic mean.
  const sortedLats = entities
    .map(e => e.latitude)
    .slice()
    .sort((a, b) => a - b)
  const sortedLons = entities
    .map(e => e.longitude)
    .slice()
    .sort((a, b) => a - b)
  const mid = Math.floor(sortedLats.length / 2)
  const medianLat = sortedLats[mid]
  const medianLng = sortedLons[mid]

  // Planar (lat, lon) distance from the median. We don't need great-circle
  // accuracy — this is purely a relative ranking.
  const distances = entities.map(e => {
    const dLat = e.latitude - medianLat
    const dLng = e.longitude - medianLng
    return Math.sqrt(dLat * dLat + dLng * dLng)
  })
  const sortedDistances = distances.slice().sort((a, b) => a - b)
  // 75th-percentile cutoff: keep three-quarters of the entities, drop the
  // furthest quarter. That's tolerant enough for cases with a few scattered
  // datapoints while still snapping tight on the main cluster.
  const cutoffIdx = Math.floor(sortedDistances.length * 0.75)
  const cutoff = sortedDistances[Math.max(0, cutoffIdx - 1)]

  let minLng = Infinity
  let maxLng = -Infinity
  let minLat = Infinity
  let maxLat = -Infinity
  for (let i = 0; i < entities.length; i++) {
    if (distances[i] > cutoff) continue
    const e = entities[i]
    if (e.longitude < minLng) minLng = e.longitude
    if (e.longitude > maxLng) maxLng = e.longitude
    if (e.latitude < minLat) minLat = e.latitude
    if (e.latitude > maxLat) maxLat = e.latitude
  }
  // If the percentile cutoff happens to filter everything out (shouldn't,
  // but be safe), fall back to all entities.
  if (!Number.isFinite(minLng)) return allBounds()
  return { minLng, minLat, maxLng, maxLat }
}

function entitiesToFeatureCollection(
  entities: GeoEntity[],
  tracks?: Record<string, TrackPoint[]>,
  trackTimestampsMs?: Record<string, number[]>,
  playheadMs?: number | null
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = []
  for (const e of entities) {
    const track = tracks?.[e.id]
    const ts = trackTimestampsMs?.[e.id]
    const t = normalizeMarkerType(e.label ?? e.type)
    const props: GeoJSON.GeoJsonProperties = {
      id: e.id,
      name: e.name,
      type: t,
      // Bare Location anchors (no in-set entity attached) render dimmed.
      dimmed: e.dimmed === true,
    }
    if (track && ts && track.length >= 2) {
      let coords: [number, number] | null = null
      if (playheadMs != null) {
        const { currentPos } = clipTrackToTime(track, ts, playheadMs)
        if (currentPos) coords = currentPos
      }
      // Fall back to the most recent known position whenever the playhead
      // gives us nothing — either it's null (no playback) or it lies before
      // the entity's first observation (Cesium-style NONE-backward would
      // hide the entity entirely; we want it visible & spider-able anyway).
      if (!coords) {
        const last = track[track.length - 1]
        coords = [last.longitude, last.latitude]
      }
      features.push({
        type: 'Feature',
        properties: props,
        geometry: { type: 'Point', coordinates: coords },
      })
    } else {
      features.push({
        type: 'Feature',
        properties: props,
        geometry: { type: 'Point', coordinates: [e.longitude, e.latitude] },
      })
    }
  }
  return { type: 'FeatureCollection', features }
}

interface BuildTracksOpts {
  entities: GeoEntity[]
  tracks: Record<string, TrackPoint[]>
  trackTimestampsMs: Record<string, number[]>
  hiddenTrackEntityIds: Set<string>
  trackColors: Record<string, string>
  showTracks: boolean
  playheadMs: number | null
}

function buildTracksFeatureCollection(opts: BuildTracksOpts): GeoJSON.FeatureCollection {
  const {
    entities,
    tracks,
    trackTimestampsMs,
    hiddenTrackEntityIds,
    trackColors,
    showTracks,
    playheadMs,
  } = opts
  if (!showTracks || playheadMs == null) {
    return { type: 'FeatureCollection', features: [] }
  }
  const features: GeoJSON.Feature[] = []
  for (const e of entities) {
    if (hiddenTrackEntityIds.has(e.id)) continue
    const track = tracks[e.id]
    const ts = trackTimestampsMs[e.id]
    if (!track || !ts || track.length < 2) continue
    const { coords } = clipTrackToTime(track, ts, playheadMs)
    if (coords.length < 2) continue
    const properties: Record<string, unknown> = {
      id: e.id,
      type: normalizeMarkerType(e.label ?? e.type),
      // Always emit a per-feature colour. Explicit override wins; otherwise
      // we spread entity ids across a 12-slot palette so two tracks of the
      // same type don't collapse onto the same default hue.
      color: trackColors[e.id] ?? defaultTrackColorFor(e.id),
    }
    features.push({
      type: 'Feature',
      properties,
      geometry: { type: 'LineString', coordinates: coords },
    })
  }
  return { type: 'FeatureCollection', features }
}

function closeRing(coords: [number, number][] | number[][]): [number, number][] {
  if (coords.length === 0) return []
  const out = coords.map(c => [c[0], c[1]] as [number, number])
  const first = out[0]
  const last = out[out.length - 1]
  if (first[0] !== last[0] || first[1] !== last[1]) {
    out.push([first[0], first[1]])
  }
  return out
}

interface GeofenceLike {
  id: string
  name: string
  color: string
  visible: boolean
  vertices: { longitude: number; latitude: number }[]
}

function geofencesToFillFc(geofences: GeofenceLike[]): GeoJSON.FeatureCollection {
  const visible = geofences.filter(g => g.visible)
  return {
    type: 'FeatureCollection',
    features: visible.map(g => ({
      type: 'Feature',
      properties: { id: g.id, name: g.name, color: g.color },
      geometry: {
        type: 'Polygon',
        coordinates: [closeRing(g.vertices.map(v => [v.longitude, v.latitude]))],
      },
    })),
  }
}

function geofencesToLineFc(geofences: GeofenceLike[]): GeoJSON.FeatureCollection {
  const visible = geofences.filter(g => g.visible)
  return {
    type: 'FeatureCollection',
    features: visible.map(g => ({
      type: 'Feature',
      properties: { id: g.id, name: g.name, color: g.color },
      geometry: {
        type: 'LineString',
        coordinates: closeRing(g.vertices.map(v => [v.longitude, v.latitude])),
      },
    })),
  }
}

// Track polyline layer — placed below the entity layers so pins/halos render
// on top. Color is data-driven by entity type so the trail of a vehicle reads
// red and an aircraft reads cyan, matching the legacy Cesium scheme.
function ensureTrackLayers(map: MapLibreMap) {
  if (!map.getSource(TRACKS_SOURCE_ID)) {
    map.addSource(TRACKS_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
  }
  // Soft glow underneath — wider, low-opacity copy that gives the trail a
  // subtle bloom against dark basemaps.
  if (!map.getLayer(TRACKS_LINE_GLOW_LAYER)) {
    map.addLayer({
      id: TRACKS_LINE_GLOW_LAYER,
      type: 'line',
      source: TRACKS_SOURCE_ID,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': TRACK_LINE_COLOR,
        'line-width': ['interpolate', ['linear'], ['zoom'], 0, 4, 12, 8, 18, 14],
        'line-opacity': 0.35,
        'line-blur': 3,
      },
    })
  }
  if (!map.getLayer(TRACKS_LINE_LAYER)) {
    map.addLayer({
      id: TRACKS_LINE_LAYER,
      type: 'line',
      source: TRACKS_SOURCE_ID,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': TRACK_LINE_COLOR,
        'line-width': ['interpolate', ['linear'], ['zoom'], 0, 1.5, 12, 3, 18, 5],
        'line-opacity': 0.95,
      },
    })
  }
}

function ensureEntityLayers(map: MapLibreMap) {
  // Main clustered source.
  if (!map.getSource(ENTITY_SOURCE_ID)) {
    map.addSource(ENTITY_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: true,
      clusterRadius: 50,
      clusterMaxZoom: 14,
    })
  }
  // Cluster discs — yellow → orange → red, sized by point_count.
  if (!map.getLayer(ENTITY_CLUSTER_LAYER)) {
    map.addLayer({
      id: ENTITY_CLUSTER_LAYER,
      type: 'circle',
      source: ENTITY_SOURCE_ID,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          CLUSTER_HEAT.small,
          10,
          CLUSTER_HEAT.mid,
          30,
          CLUSTER_HEAT.large,
          100,
          CLUSTER_HEAT.xlarge,
        ],
        'circle-radius': ['step', ['get', 'point_count'], 22, 10, 30, 30, 38, 100, 48],
        'circle-stroke-color': 'rgba(8,9,12,0.55)',
        'circle-stroke-width': 1.25,
        'circle-stroke-opacity': 0.9,
        'circle-blur': 0.05,
      },
    })
  }

  // Cluster count text — drawn on top of the cluster disc, white with
  // a strong dark halo so it reads against any cluster colour.
  if (!map.getLayer(ENTITY_CLUSTER_COUNT_LAYER)) {
    map.addLayer({
      id: ENTITY_CLUSTER_COUNT_LAYER,
      type: 'symbol',
      source: ENTITY_SOURCE_ID,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['Noto Sans Bold'],
        'text-size': ['step', ['get', 'point_count'], 14, 10, 16, 30, 19, 100, 22],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.7)',
        'text-halo-width': 2,
      },
    })
  }

  // Selected halo — soft radial glow that fades to transparent. No hard
  // outline. Two stacked discs: wider/lower-alpha outer + tighter/brighter
  // inner, both heavily blurred for a real shadow-gradient feel. They live
  // on the SAME clustered entity source filtered to the selected feature,
  // so when the selected pin gets clustered on zoom-out the filter matches
  // nothing and the halo disappears with the underlying pin.
  // Halo layers go BEFORE the pin layer so the pin renders on top.
  if (!map.getLayer(ENTITY_SELECTED_HALO_LAYER)) {
    const haloTranslate = [
      'interpolate',
      ['linear'],
      ['zoom'],
      0,
      ['literal', [0, -22]],
      8,
      ['literal', [0, -28]],
      14,
      ['literal', [0, -34]],
      18,
      ['literal', [0, -42]],
    ] as unknown as maplibregl.ExpressionSpecification

    // Default filter matches no feature — overridden in the data-push
    // effect once we know the current selection.
    // biome-ignore lint/suspicious/noExplicitAny: maplibre filter typing
    const noopFilter: any = ['==', ['literal', '__none__'], ['literal', '__match__']]

    // Outer soft cushion — wider, with stronger alpha so the selection
    // reads from across the map without becoming a hard ring.
    map.addLayer({
      id: ENTITY_SELECTED_HALO_OUTER_LAYER,
      type: 'circle',
      source: ENTITY_SOURCE_ID,
      filter: noopFilter,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 42, 8, 54, 14, 70, 18, 88],
        'circle-color': 'rgba(224,144,80,0.40)',
        'circle-blur': 0.85,
        'circle-stroke-width': 0,
        'circle-translate': haloTranslate,
        'circle-translate-anchor': 'viewport',
      },
    })

    // Inner bright glow — punchy core that picks the pin out at a glance
    // even on busy basemaps. Still soft-edged via blur, no hard outline.
    map.addLayer({
      id: ENTITY_SELECTED_HALO_LAYER,
      type: 'circle',
      source: ENTITY_SOURCE_ID,
      filter: noopFilter,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 26, 8, 32, 14, 42, 18, 54],
        'circle-color': 'rgba(224,144,80,0.78)',
        'circle-blur': 0.6,
        'circle-stroke-width': 0,
        'circle-translate': haloTranslate,
        'circle-translate-anchor': 'viewport',
      },
    })
  }

  // Unclustered points — classic teardrop pin in entity-type colour, with
  // the type's Lucide glyph in the bell. Anchored at 'bottom' so the pin
  // tip lands exactly on the geo coordinate. Added LAST so pins render on
  // top of the halo glow.
  if (!map.getLayer(ENTITY_POINT_LAYER)) {
    map.addLayer({
      id: ENTITY_POINT_LAYER,
      type: 'symbol',
      source: ENTITY_SOURCE_ID,
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': ['concat', 'pin-', ['get', 'type']],
        'icon-anchor': 'bottom',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 0, 0.55, 8, 0.7, 14, 0.85, 18, 1.05],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: {
        // Bare Location anchors render at reduced opacity to read as a
        // "valid but unexplored place" vs. a fully attached entity pin.
        'icon-opacity': ['case', ['==', ['get', 'dimmed'], true], 0.45, 1],
      },
    })
  }
}

// Build the per-type pin bitmaps and register them as map images. Called on
// initial map load and after every setStyle (which clears the registry).
async function registerPinImages(map: MapLibreMap): Promise<void> {
  const records = await buildAllPinImages()
  for (const { name, data, pixelRatio } of records) {
    if (!map.hasImage(name)) {
      map.addImage(name, data, { pixelRatio })
    }
  }
}

// Spider expander layers — connector legs (dashed line from stack centre
// to each spread pin) + the spread pins themselves. Both empty until the
// user opens the spider on a stack click.
function ensureSpiderLayers(map: MapLibreMap) {
  if (!map.getSource(SPIDER_LEG_SOURCE)) {
    map.addSource(SPIDER_LEG_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
  }
  if (!map.getSource(SPIDER_POINT_SOURCE)) {
    map.addSource(SPIDER_POINT_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
  }
  if (!map.getLayer(SPIDER_LEG_LAYER)) {
    map.addLayer({
      id: SPIDER_LEG_LAYER,
      type: 'line',
      source: SPIDER_LEG_SOURCE,
      paint: {
        'line-color': '#e09050',
        'line-width': 1.4,
        'line-opacity': 0.7,
        'line-dasharray': [2, 2],
      },
    })
  }
  // Selected-halo layers for the spider: same paint as the entity-source
  // selected halos, but driven by the spread pins' source so the glow
  // follows the selected entity to its fanned-out position. Default filter
  // matches no feature — overridden when the selection / spider changes.
  // biome-ignore lint/suspicious/noExplicitAny: maplibre filter typing
  const spiderHaloNoop: any = ['==', ['literal', '__none__'], ['literal', '__match__']]
  const spiderHaloTranslate = [
    'interpolate',
    ['linear'],
    ['zoom'],
    0,
    ['literal', [0, -22]],
    8,
    ['literal', [0, -28]],
    14,
    ['literal', [0, -34]],
    18,
    ['literal', [0, -42]],
  ] as unknown as maplibregl.ExpressionSpecification
  // Insert the halo circles below the spider pin symbol so the pin always
  // renders on top. When the symbol layer doesn't exist yet (first call),
  // beforeId stays undefined and the halos get appended, then the symbol
  // layer is appended after — preserving the same ordering.
  const haloBeforeId = map.getLayer(SPIDER_POINT_LAYER) ? SPIDER_POINT_LAYER : undefined
  if (!map.getLayer(SPIDER_SELECTED_HALO_OUTER_LAYER)) {
    map.addLayer(
      {
        id: SPIDER_SELECTED_HALO_OUTER_LAYER,
        type: 'circle',
        source: SPIDER_POINT_SOURCE,
        filter: spiderHaloNoop,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 42, 8, 54, 14, 70, 18, 88],
          'circle-color': 'rgba(224,144,80,0.40)',
          'circle-blur': 0.85,
          'circle-stroke-width': 0,
          'circle-translate': spiderHaloTranslate,
          'circle-translate-anchor': 'viewport',
        },
      },
      haloBeforeId
    )
  }
  if (!map.getLayer(SPIDER_SELECTED_HALO_LAYER)) {
    map.addLayer(
      {
        id: SPIDER_SELECTED_HALO_LAYER,
        type: 'circle',
        source: SPIDER_POINT_SOURCE,
        filter: spiderHaloNoop,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 26, 8, 32, 14, 42, 18, 54],
          'circle-color': 'rgba(224,144,80,0.78)',
          'circle-blur': 0.6,
          'circle-stroke-width': 0,
          'circle-translate': spiderHaloTranslate,
          'circle-translate-anchor': 'viewport',
        },
      },
      haloBeforeId
    )
  }
  if (!map.getLayer(SPIDER_POINT_LAYER)) {
    map.addLayer({
      id: SPIDER_POINT_LAYER,
      type: 'symbol',
      source: SPIDER_POINT_SOURCE,
      layout: {
        'icon-image': ['concat', 'pin-', ['get', 'type']],
        'icon-anchor': 'bottom',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 0, 0.55, 8, 0.7, 14, 0.85, 18, 1.05],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: {
        // Match the un-spidered point layer: bare Location anchors stay dimmed.
        'icon-opacity': ['case', ['==', ['get', 'dimmed'], true], 0.45, 1],
      },
    })
  }
}

function ensureGeofenceLayers(map: MapLibreMap) {
  if (!map.getSource(GEOFENCE_FILL_SOURCE)) {
    map.addSource(GEOFENCE_FILL_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
  }
  if (!map.getSource(GEOFENCE_LINE_SOURCE)) {
    map.addSource(GEOFENCE_LINE_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
  }
  if (!map.getLayer(GEOFENCE_FILL_LAYER)) {
    map.addLayer({
      id: GEOFENCE_FILL_LAYER,
      type: 'fill',
      source: GEOFENCE_FILL_SOURCE,
      paint: {
        'fill-color': ['coalesce', ['get', 'color'], '#f59e0b'],
        'fill-opacity': 0.18,
      },
    })
  }
  if (!map.getLayer(GEOFENCE_LINE_LAYER)) {
    map.addLayer({
      id: GEOFENCE_LINE_LAYER,
      type: 'line',
      source: GEOFENCE_LINE_SOURCE,
      paint: {
        'line-color': ['coalesce', ['get', 'color'], '#f59e0b'],
        'line-width': 2,
        'line-opacity': 0.9,
      },
    })
  }
  if (!map.getLayer(GEOFENCE_LABEL_LAYER)) {
    map.addLayer({
      id: GEOFENCE_LABEL_LAYER,
      type: 'symbol',
      source: GEOFENCE_FILL_SOURCE,
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 11,
        'symbol-placement': 'point',
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.85)',
        'text-halo-width': 1.4,
      },
    })
  }
}

function ensureDrawLayers(map: MapLibreMap) {
  for (const id of [
    DRAW_FILL_SOURCE,
    DRAW_LINE_SOURCE,
    DRAW_VERTEX_SOURCE,
    `${DRAW_LINE_SOURCE}-preview`,
  ]) {
    if (!map.getSource(id)) {
      map.addSource(id, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
    }
  }
  if (!map.getLayer(DRAW_FILL_LAYER)) {
    map.addLayer({
      id: DRAW_FILL_LAYER,
      type: 'fill',
      source: DRAW_FILL_SOURCE,
      paint: { 'fill-color': '#f4a847', 'fill-opacity': 0.15 },
    })
  }
  if (!map.getLayer(DRAW_LINE_LAYER)) {
    map.addLayer({
      id: DRAW_LINE_LAYER,
      type: 'line',
      source: DRAW_LINE_SOURCE,
      paint: { 'line-color': '#f4a847', 'line-width': 2 },
    })
  }
  if (!map.getLayer(DRAW_PREVIEW_LINE_LAYER)) {
    map.addLayer({
      id: DRAW_PREVIEW_LINE_LAYER,
      type: 'line',
      source: `${DRAW_LINE_SOURCE}-preview`,
      paint: {
        'line-color': '#f4a847',
        'line-width': 1.5,
        'line-dasharray': [2, 2],
        'line-opacity': 0.75,
      },
    })
  }
  if (!map.getLayer(DRAW_VERTEX_LAYER)) {
    map.addLayer({
      id: DRAW_VERTEX_LAYER,
      type: 'circle',
      source: DRAW_VERTEX_SOURCE,
      paint: {
        'circle-radius': 5,
        'circle-color': '#f4a847',
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.5,
      },
    })
  }
}
